import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './Admin.css';

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [newUser, setNewUser] = useState({
    role: 'candidate',
    name: '',
    email: '',
    phone: '',
    password: '',
    companyWebsite: '',
    gstNumber: '',
    panNumber: ''
  });

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, [filter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        role: filter !== 'all' ? filter : undefined,
        search: searchTerm || undefined
      };
      const response = await api.get('/admin/users', { params });
      // console.log('API Response:', response); // Debug log
      // console.log('Users Data:', response.data); // Debug users array
      if (response.data && response.data.length > 0) {
        // console.log('First User:', response.data[0]); // Debug first user
      }
      setUsers(response.data || []);
      setPagination(response.pagination || {});
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      const errorMessage =
        error.error?.message ||
        error.message ||
        'Failed to fetch users';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await api.patch(`/admin/users/${userId}/status`, {
        status: newStatus
      });
      toast.success('User status updated');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMessage =
        error.error?.message ||
        error.message ||
        'Failed to update user status';
      toast.error(errorMessage);
    }
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      toast.error('Please provide a suspension reason');
      return;
    }

    try {
      await api.patch(`/admin/users/${selectedUser._id}/suspend`, {
        reason: suspendReason
      });
      toast.success('User suspended successfully');
      setShowSuspendModal(false);
      setSuspendReason('');
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error suspending user:', error);
      const errorMessage =
        error.error?.message ||
        error.message ||
        'Failed to suspend user';
      toast.error(errorMessage);
    }
  };

  const handleActivate = async (userId) => {
    try {
      await api.patch(`/admin/users/${userId}/activate`);
      toast.success('User activated successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error activating user:', error);
      const errorMessage =
        error.error?.message ||
        error.message ||
        'Failed to activate user';
      toast.error(errorMessage);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: 'badge badge-danger',
      admin: 'badge badge-warning',
      subadmin: 'badge badge-info',
      employer: 'badge badge-primary',
      candidate: 'badge badge-success',
      consultant: 'badge badge-secondary'
    };
    return badges[role] || 'badge badge-secondary';
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!newUser.name || !newUser.email || !newUser.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await api.post('/admin/users/create', newUser);
      toast.success('User created successfully');
      setShowCreateModal(false);
      setNewUser({
        role: 'candidate',
        name: '',
        email: '',
        phone: '',
        password: '',
        companyWebsite: '',
        gstNumber: '',
        panNumber: ''
      });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      // API interceptor already extracts error.response.data
      // So error object is already the data from response
      const errorMessage =
        error.error?.message ||           // From API error format: { error: { message: '...' } }
        error.message ||                  // Direct message property
        error.response?.data?.error?.message ||  // Fallback for non-intercepted errors
        error.response?.data?.message ||  // Another fallback
        'Failed to create user';          // Final fallback
      toast.error(errorMessage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>Manage Users</h1>
          <p>View and manage all registered users</p>
        </div>
        {/* <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          ➕ Create User
        </button> */}
      </div>

      {/* Search and Filters */}
      <div className="filters-section">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-primary">
            Search
          </button>
        </form>

        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Users
          </button>
          <button
            className={`filter-tab ${filter === 'candidate' ? 'active' : ''}`}
            onClick={() => setFilter('candidate')}
          >
            Candidates
          </button>
          <button
            className={`filter-tab ${filter === 'employer' ? 'active' : ''}`}
            onClick={() => setFilter('employer')}
          >
            Employers
          </button>
          <button
            className={`filter-tab ${filter === 'consultant' ? 'active' : ''}`}
            onClick={() => setFilter('consultant')}
          >
            Consultants
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Created By</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user._id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <span style={{fontWeight:'500'}}>{user.name}</span>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <div className={` ${getRoleBadge(user.role)}`}>
                      {user.role || 'N/A'}
                    </div>
                  </td>
                  <td>{user.phone || 'N/A'}</td>
                  <td>
                    <div className={` ${
                      user.status === 'active' ? 'badge badge-success' :
                      user.status === 'suspended' ? 'badge badge-danger' :
                      user.status === 'inactive' ? 'badge badge-warning' :
                      user.status === 'pending_verification' ? 'badge badge-info' :
                      'badge-secondary'
                    }`}>
                      {user.status || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className={` ${user.createdBy === 'admin' ? 'badge badge-warning' : 'badge badge-info'}`}>
                      {user.createdBy === 'admin' ? 'Admin' : user.createdBy === 'self' ? 'Self' : 'N/A'}
                    </div>
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      {user.status === 'suspended' ? (
                        <button
                          className="btn btn-success"
                          onClick={() => handleActivate(user._id)}
                        >
                          Activate
                        </button>
                      ) : (
                        <>
                          <button
                            className={`btn ${user.status === 'active' ? 'btn-warning' : 'btn-success'}`}
                            onClick={() => handleStatusToggle(user._id, user.status)}
                          >
                            {user.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowSuspendModal(true);
                            }}
                          >
                            Suspend
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-sm"
            disabled={pagination.page === 1}
            onClick={() => {/* Previous page */}}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            className="btn btn-sm"
            disabled={pagination.page === pagination.pages}
            onClick={() => {/* Next page */}}
          >
            Next
          </button>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{borderBottom:'none', marginBottom:'0px'}}>
              <h2>Create New User</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCreateUser}>
                <div className="form-group">
                  <label>Role *</label>
                  <select
                    name="role"
                    value={newUser.role}
                    onChange={handleInputChange}
                    className="form-select"
                    required
                  >
                    <option value="candidate">Candidate</option>
                    <option value="employer">Employer</option>
                    <option value="consultant">Consultant</option>
                  </select>
                  <p className="help-text">
                    {newUser.role === 'candidate' && '✓ Auto-verified after creation'}
                    {newUser.role === 'employer' && '✓ Will be verified by admin (you)'}
                    {newUser.role === 'consultant' && '✓ Will be verified by admin (you)'}
                  </p>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={newUser.name}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={newUser.email}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={newUser.phone}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="+91 9876543210"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Password (Optional)</label>
                    <input
                      type="password"
                      name="password"
                      value={newUser.password}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Leave blank for default"
                    />
                    <p className="help-text">Default: TempPassword@123</p>
                  </div>
                </div>

                {(newUser.role === 'employer' || newUser.role === 'consultant') && (
                  <>
                    <div className="form-group">
                      <label>Company Website</label>
                      <input
                        type="url"
                        name="companyWebsite"
                        value={newUser.companyWebsite}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="https://company.com"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>GST Number</label>
                        <input
                          type="text"
                          name="gstNumber"
                          value={newUser.gstNumber}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="22AAAAA0000A1Z5"
                        />
                      </div>

                      <div className="form-group">
                        <label>PAN Number</label>
                        <input
                          type="text"
                          name="panNumber"
                          value={newUser.panNumber}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="ABCDE1234F"
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Suspend User Modal */}
      {showSuspendModal && (
        <div className="modal-overlay" onClick={() => setShowSuspendModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{borderBottom:'none', marginBottom:'0px'}}>
              <h2>Suspend User</h2>
              <button className="modal-close" onClick={() => setShowSuspendModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning">
                <strong>⚠️ Warning:</strong> Suspending user <strong>{selectedUser?.name}</strong> will prevent them from accessing the platform.
              </div>
              <form onSubmit={(e) => { e.preventDefault(); handleSuspend(); }}>
                <div className="form-group">
                  <label>Suspension Reason *</label>
                  <textarea
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter reason for suspension..."
                    required
                  />
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setShowSuspendModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-danger">
                    Suspend User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;
